import { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useApiClient } from "../../api/ApiClientContext";
import type { SharingDetail } from "../../api/types";
import { ApiError } from "../../api/types";
import { StaffSharingFilesTab } from "./StaffSharingFilesTab";
import { StaffSharingSettingsTab } from "./StaffSharingSettingsTab";

type DetailTab = "files" | "settings";

function tabFromQuery(value: string | null): DetailTab {
  return value === "settings" ? "settings" : "files";
}

/**
 * One sharing's detail page: a header (name, open/closed) and two tabs
 * (?tab=files|settings) - a files explorer (files-explorer-reference.png
 * layout) and the members/allowlist/close management page. The sharing
 * code is never displayed here (manager decision, 2026-09-11): it stays
 * in the URL (`code`, used for every API call) but is not shown as text.
 * The tab lives in the URL for the same reason every other explorer state
 * does: a refresh or a deep link must land on the same view.
 */
export function StaffSharingDetailPage() {
  const apiClient = useApiClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { code = "" } = useParams<{ code: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = tabFromQuery(searchParams.get("tab"));

  // The sharings list's own URL (path + query), handed to us as router
  // state when navigation started there (StaffSharingsPage's row link,
  // row click and "Open" menu action): every way back to the list
  // (breadcrumb, back button, a direct deep link's fallback) returns
  // there, preserving whatever state that URL carries (manager decision,
  // 2026-09-11). Falls back to the plain list route for a deep link that
  // never went through the list first.
  const backTo = (location.state as { backTo?: string } | null)?.backTo ?? "/staff/sharings";

  const [detail, setDetail] = useState<SharingDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Manual refresh (after every mutation in either tab): a plain event
  // handler, not an effect, so setting loading synchronously here is fine.
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setDetail(await apiClient.getSharing(code));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not load this sharing.");
    } finally {
      setLoading(false);
    }
  }, [apiClient, code]);

  // Initial load on mount: `loading` already starts true, and every
  // setState below only runs after `await apiClient.getSharing(code)`
  // resolves, so none is reachable synchronously from this effect body
  // (react-hooks/set-state-in-effect), same pattern as AuthContext's
  // initial session load.
  useEffect(() => {
    let ignore = false;
    void (async () => {
      try {
        const result = await apiClient.getSharing(code);
        if (!ignore) {
          setDetail(result);
          setError(null);
        }
      } catch (caught) {
        if (!ignore) {
          setError(caught instanceof ApiError ? caught.message : "Could not load this sharing.");
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    })();
    return () => {
      ignore = true;
    };
  }, [apiClient, code]);

  function handleTabChange(nextTab: DetailTab) {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.set("tab", nextTab);
        // The files explorer's own folder/sort state does not carry over
        // between sharings' tabs; drop it so switching tabs never shows a
        // stale folder selection.
        next.delete("folder");
        next.delete("sort");
        next.delete("dir");
        return next;
      },
      { replace: true },
    );
  }

  if (loading && !detail) {
    return (
      <Stack sx={{ alignItems: "center", justifyContent: "center", py: 8 }}>
        <CircularProgress aria-label="Loading sharing" />
      </Stack>
    );
  }

  if (error && !detail) {
    return (
      <Box sx={{ p: { xs: 2, sm: 4 } }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button onClick={() => navigate(backTo)}>Back to Sharings</Button>
      </Box>
    );
  }

  if (!detail) {
    return null;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, minHeight: 0 }}>
      <Box sx={{ px: { xs: 2, sm: 4 }, pt: { xs: 2, sm: 3 } }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
          <Link component={RouterLink} to={backTo} underline="hover" color="inherit">
            Sharings
          </Link>
          <Typography color="text.primary">{detail.displayName}</Typography>
        </Breadcrumbs>

        <Stack direction="row" spacing={2} sx={{ alignItems: "center", mb: 2 }}>
          <Button
            component={RouterLink}
            to={backTo}
            size="small"
            startIcon={<ArrowBackIcon fontSize="small" />}
          >
            Back to sharings
          </Button>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
            {detail.displayName}
          </Typography>
          {detail.closed ? (
            <Chip label="Closed" size="small" />
          ) : (
            <Chip label="Open" size="small" color="success" />
          )}
        </Stack>

        {/*
          A refresh after the initial load (for example after adding a
          member) can still fail; unlike the early-return above, `detail`
          is already set by then, so this Alert is the only place that
          error is ever shown.
        */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Tabs value={tab} onChange={(_event, value: DetailTab) => handleTabChange(value)}>
          <Tab label="Files" value="files" />
          <Tab label="Members & settings" value="settings" />
        </Tabs>
      </Box>

      {tab === "files" ? (
        <StaffSharingFilesTab code={code} detail={detail} onRefresh={load} />
      ) : (
        <Box sx={{ px: { xs: 2, sm: 4 }, py: 3 }}>
          <StaffSharingSettingsTab code={code} detail={detail} onRefresh={load} />
        </Box>
      )}
    </Box>
  );
}
